import {
  nonReviewDraftMarkStatuses,
  reviewMarkStatuses
} from 'helpers/domain-specific/mark-status.helpers';
import type { AuthorisableMark } from 'policies/entities/mark';
import { type AuthorisableUser, hasRole } from 'policies/entities/user';
import { ROLE } from 'services/assist-api/entities/role/roles';
import {
  MarkStatus,
  MarkType
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { markingAllocationRoles } from './marking-allocation.policy';

// These roles are restricted to only marking responses that they have been explictly allocated to:
export const explicitlyAllocatedMarkerRoles = [ROLE.MARKER];

// These roles can freely change marks that they have access to and will be automatically allocated
// to the response and set as the active marker when saving the form:
export const independentMarkerRoles = [ROLE.ADMIN, ROLE.TEACHER];

// Helper functions (don't use these directly for authorising actions; use a policy action):
export function userIsAllocatedToResponse(user: AuthorisableUser, mark: AuthorisableMark): boolean {
  return mark.responseMarkers.find((m) => m.user.id === user.customerUser?.id) !== undefined;
}

export function userIsActiveMarker(user: AuthorisableUser, mark: AuthorisableMark): boolean {
  const currentResponseMarker = mark.responseMarkers.find(
    (m) => m.user.id === user.customerUser?.id
  );
  if (mark.id) {
    return mark.activeMarkerId === currentResponseMarker?.id;
  }
  // We can consider the active marker to be the current user if there is no mark.
  return currentResponseMarker !== undefined;
}

/**
 * Authorisation rules for the resource that the policy is named after.
 * Functions return true only if the user can perform the action.
 * Follows the policy pattern used in Gate (Laravel) and Pundit (Rails).
 */
export default class MarkPolicy {
  /**
   * Permits action if user can view a Mark Response page, but not necessarily the details of the
   * mark.
   */
  static viewPage(user: AuthorisableUser): boolean {
    if (!user.belongsToMarkingOrganisation) return false;
    return markingAllocationRoles
      .concat(independentMarkerRoles)
      .concat(explicitlyAllocatedMarkerRoles)
      .some((role) => user.roles.includes(role));
  }

  /**
   * Permits action if user can view the Mark Response form and all details displayed on it.
   */
  static viewForm(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    // Permits action if user has a marking allocation role or an independent (implicitly allocated)
    // marker role, regardless of being allocated:
    if (hasRole(user, markingAllocationRoles.concat(independentMarkerRoles))) return true;

    // Permits action if the user is both allocated to the response and has an explictly allocated
    // marker role:
    return hasRole(user, explicitlyAllocatedMarkerRoles) && userIsAllocatedToResponse(user, mark);
  }

  /**
   * Permits action if user can create or edit a Mark and any mark items, but not necessarily
   * override the status.
   */
  static edit(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    // Forbids action if the mark with automatically marked, regardless of role.
    if (mark.type === MarkType.Autoscored) return false;

    // Forbids action if the user is not the active marker (unless the user has a marking allocation
    // role or is an independent marker who will be implicitly allocated).
    if (!hasRole(user, markingAllocationRoles.concat(independentMarkerRoles))) {
      if (!userIsActiveMarker(user, mark)) return false;
    }

    // Permits action if the mark has a review status and the user has a marking allocation role.
    // This allows admins to make changes to the mark for the duration of the review process.
    if (
      hasRole(user, markingAllocationRoles) &&
      mark.status !== undefined &&
      reviewMarkStatuses.includes(mark.status)
    ) {
      return true;
    }

    // Permits action if the mark has an editing status:
    return (
      mark.status !== undefined &&
      [MarkStatus.Assigned].concat(nonReviewDraftMarkStatuses).includes(mark.status)
    );
  }

  /**
   * Permits action if the user can be allocated to the response and set as the active marker of
   * the mark without being explicitly allocated first or a confirmation modal appearing to confirm
   * the allocation on save. This ensures that marking is not restricted for these users based on
   * ownership.
   */
  static markIndependently(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    return hasRole(user, independentMarkerRoles) && mark.type === MarkType.Manual;
  }

  /**
   * Permits action if the user can administrate the mark, e.g. by overriding the status.
   */
  static administrate(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    return hasRole(user, markingAllocationRoles) && mark.type === MarkType.Manual;
  }

  /**
   * Permits action if user can delete the mark.
   */
  static delete(user: AuthorisableUser, mark: AuthorisableMark): boolean {
    return (
      hasRole(user, markingAllocationRoles.concat(independentMarkerRoles)) &&
      mark.type === MarkType.Manual &&
      mark.id !== undefined
    );
  }
}
